import React, { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const CloudinaryUploadWidget = ({ onUploadSuccess, buttonText = 'Upload Image', folder }) => {
  const cloudinaryWidget = useRef(null);
  const fileInputRef = useRef(null);
  const [configError, setConfigError] = useState(false);
  // Use environment variables or fallback to these values for development
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  useEffect(() => {
    // Check if Cloudinary config is available
    if (!cloudName || !uploadPreset || cloudName === 'not_set' || uploadPreset === 'not_set') {
      console.warn('Cloudinary configuration missing. Using fallback file input.');
      setConfigError(true);
      return;
    }

    // Load the Cloudinary script if it hasn't been loaded yet
    if (!window.cloudinary) {
      const script = document.createElement('script');
      script.src = 'https://upload-widget.cloudinary.com/global/all.js';
      script.async = true;
      script.onload = () => {
        initializeWidget();
      };
      document.body.appendChild(script);
    } else {
      initializeWidget();
    }

    return () => {
      // Clean up the widget instance when the component unmounts
      if (cloudinaryWidget.current) {
        cloudinaryWidget.current.destroy();
        cloudinaryWidget.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  const initializeWidget = () => {
    if (window.cloudinary && cloudName && uploadPreset) {
      const widgetOptions = {
        cloudName: cloudName,
        uploadPreset: uploadPreset,
        sources: ['local', 'url', 'camera', 'image_search', 'google_drive'],
        multiple: false, // Set to true if you want to allow multiple file uploads
        cropping: true, // Set to true to enable cropping
        // croppingAspectRatio: 1, // Optional: Enforce aspect ratio for cropping
        // showAdvancedOptions: true, // Optional: Show advanced options
        // styles: { ... }, // Optional: Custom styles
      };
      if (folder) {
        widgetOptions.folder = folder; // Optional: specify a folder in Cloudinary
      }

      cloudinaryWidget.current = window.cloudinary.createUploadWidget(
        widgetOptions,
        (error, result) => {
          if (!error && result && result.event === 'success') {
            console.log('Done! Here is the image info: ', result.info);
            if (onUploadSuccess) {
              onUploadSuccess(result.info.secure_url, result.info.public_id);
            }
          }
          if (error) {
            console.error('Cloudinary Upload Error:', error);
            setConfigError(true); // Fall back to file input if there's an error
          }
        }
      );
    } else {
      console.error('Cloudinary configuration missing. Check .env file and restart server.');
      setConfigError(true);
    }
  };

  const openWidget = () => {
    if (configError) {
      // Use the fallback file input if there's a configuration error
      fileInputRef.current?.click();
      return;
    }
    
    if (cloudinaryWidget.current) {
      cloudinaryWidget.current.open();
    } else {
      console.error('Cloudinary widget is not initialized.');
      // Attempt to initialize again if not ready, could be due to script loading delay
      initializeWidget();
      // If initialization fails, fall back to file input
      if (!cloudinaryWidget.current) {
        setConfigError(true);
        fileInputRef.current?.click();
      }
    }
  };
  
  // Fallback file handler for when Cloudinary is not configured
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Create a temporary URL for the file
    const tempUrl = URL.createObjectURL(file);
    console.log('Using fallback file upload, created temporary URL:', tempUrl);
    
    // Call the success handler with the temporary URL
    if (onUploadSuccess) {
      onUploadSuccess(tempUrl, `temp_${Date.now()}`);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        component="label"
        startIcon={<CloudUploadIcon />}
        onClick={openWidget}
        sx={{ mt: 1, mb: 1 }}
      >
        {buttonText}
      </Button>
      {/* Hidden file input as fallback when Cloudinary is not configured */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileChange}
      />
    </>
  );
};

export default CloudinaryUploadWidget;
